import { useMemo } from 'react'
import PropTypes from 'prop-types'

import { useModals, useSetState } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'

import BrandSettingsModal from '@modals/BrandSettingsModal'
import BulkCreateBrandProviderModal from '@modals/BulkCreateBrandProviderModal'
import CreateUserModal from '@modals/CreateUserModal/CreateUserModal'
import ManageBrandSSOModal from '@modals/ManageBrandSSOModal'
import ManageBrandTemplateDevelopersModal from '@modals/ManageBrandTemplateDevelopersModal'
import ManageUserTeamMemberRolesModal from '@modals/ManageUserTeamMemberRolesModal'

import PageContent from './components/PageContent'

const callbacks = (component, setState) => {
  const componentCallbacks = {
    BrandSettingsModal: {
      closeModal: () => setState({ showBrandSettingsModal: false }),
      updateBrand: payload => handleCallbackAction(payload),
    },
    BulkCreateBrandProviderModal: {
      closeModal: () => setState({ showBulkCreateBrandProviderModal: false }),
      bulkCreateBrandProviders: payload => handleCallbackAction(payload),
    },
    CreateUserModal: {
      closeModal: () => setState({ showCreateUserModal: false }),
      createUser: payload => handleCallbackAction(payload),
    },
    ManageBrandSSOModal: {
      closeModal: () => setState({ showManageBrandSSOModal: false }),
      updateBrand: payload => handleCallbackAction(payload),
    },
    ManageBrandTemplateDevelopersModal: {
      closeModal: () => setState({ showManageBrandTemplateDevelopersModal: false }),
      updateBrand: payload => handleCallbackAction(payload),
    },
    ManageUserTeamMemberRolesModal: {
      closeModal: () => setState({ showManageUserTeamMemberRolesModal: false }),
      updateUser: payload => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const defaultState = {
  showBrandSettingsModal: false,
  showBulkCreateBrandProviderModal: false,
  showCreateUserModal: false,
  showManageBrandSSOModal: false,
  showManageBrandTemplateDevelopersModal: false,
  showManageUserTeamMemberRolesModal: false,
}

const Relations = (props) => {
  const { brandId } = props

  const [state, setState] = useSetState(defaultState)
  const {
    showBrandSettingsModal,
    showBulkCreateBrandProviderModal,
    showCreateUserModal,
    showManageBrandSSOModal,
    showManageBrandTemplateDevelopersModal,
    showManageUserTeamMemberRolesModal,
  } = state

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
    topModalKey,
  } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      exportBrand: payload => handleCallbackAction(payload),
      showBrandSettingsModal: (payload) => {
        setModalData('BrandSettingsModal', payload)
        setState({ showBrandSettingsModal: true })
      },
      showBulkCreateBrandProviderModal: (payload) => {
        setModalData('BulkCreateBrandProviderModal', payload)
        setState({ showBulkCreateBrandProviderModal: true })
      },
      showCreateUserModal: (payload) => {
        setModalData('CreateUserModal', payload)
        setState({ showCreateUserModal: true })
      },
      showManageBrandSSOModal: (payload) => {
        setModalData('ManageBrandSSOModal', payload)
        setState({ showManageBrandSSOModal: true })
      },
      showManageBrandTemplateDevelopersModal: (payload) => {
        setModalData('ManageBrandTemplateDevelopersModal', payload)
        setState({ showManageBrandTemplateDevelopersModal: true })
      },
      showManageUserTeamMemberRolesModal: (payload) => {
        setModalData('ManageUserTeamMemberRolesModal', payload)
        setState({ showManageUserTeamMemberRolesModal: true })
      },
      updateBrand: payload => handleCallbackAction(payload),
    },
  }), [topModalKey])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent brandId={brandId} />

        <BrandSettingsModal
          callbacks={callbacks('BrandSettingsModal', setState)}
          showModal={showBrandSettingsModal}
        />

        <BulkCreateBrandProviderModal
          callbacks={callbacks('BulkCreateBrandProviderModal', setState)}
          showModal={showBulkCreateBrandProviderModal}
        />

        <CreateUserModal
          callbacks={callbacks('CreateUserModal', setState)}
          showModal={showCreateUserModal}
        />

        <ManageBrandSSOModal
          callbacks={callbacks('ManageBrandSSOModal', setState)}
          showModal={showManageBrandSSOModal}
        />

        <ManageBrandTemplateDevelopersModal
          callbacks={callbacks('ManageBrandTemplateDevelopersModal', setState)}
          showModal={showManageBrandTemplateDevelopersModal}
        />

        <ManageUserTeamMemberRolesModal
          callbacks={callbacks('ManageUserTeamMemberRolesModal', setState)}
          showModal={showManageUserTeamMemberRolesModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

Relations.propTypes = {
  brandId: PropTypes.number.isRequired,
}

export default Relations
